import React, { useState } from 'react';
import { Card, Button, Space, message } from 'antd';
import { DownloadOutlined, ToolOutlined } from '@ant-design/icons';
import ReglementsList from '../components/reg/RegList';
import SingleReglement from '../components/reg/SingleReg';
import ClientList from '../components/client/ClientList';
import ModifyReglementForm from '../components/reg/ModifyRegForm';
import DeleteReglementButton from '../components/reg/DeleteRegButton';
import OperationProgressModal from '../components/reg/OperationProgressModal';
import { useSeedFacven, useCorrectUnifiedFactureAvoir } from '../hooks/regHooks';

const RegManagementPage = () => {
  const [currentView, setCurrentView] = useState('client-select'); // Default view is client selection
  const [selectedReglementId, setSelectedReglementId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [listClientFilter, setListClientFilter] = useState(null);
  const [previousView, setPreviousView] = useState(null); // Track where we came from

  // Modal state for showing what's happening on the server
  const [modalVisible, setModalVisible] = useState(false);
  const [modalOperation, setModalOperation] = useState(null); // 'seedFacven' | 'correctUnified'
  const [modalPhase, setModalPhase] = useState(null);
  const [modalResults, setModalResults] = useState(null);
  
  const { handleSeed, isSeeding } = useSeedFacven();
  const { handleCorrect, isCorrecting } = useCorrectUnifiedFactureAvoir();

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setCurrentView('draft'); // Show SingleReglement in draft mode
  };

  const handleSelectReglement = (id) => {
    setPreviousView('list'); // Track that we came from list
    setSelectedReglementId(id);
    setCurrentView('view');
  };

  const handleChangeView = (view, id, clientCode) => {
    setCurrentView(view);
    setSelectedReglementId(id);
    if (view === 'list') {
      setListClientFilter(clientCode || null);
      // If we're going to list from draft, preserve the selectedClient
      if (selectedClient && clientCode && String(selectedClient.code_clt || selectedClient.CODE_CLT) === String(clientCode)) {
        // Keep selectedClient for when we come back
      }
    }
  };

  const handleDraftCompleted = (reglementId) => {
    setSelectedReglementId(reglementId);
    setCurrentView('view');
  };

  const handleBackToClientSelect = () => {
    setSelectedClient(null);
    setSelectedReglementId(null);
    setCurrentView('client-select');
  };

  const handleSeedFacven = async () => {
    setModalVisible(true);
    setModalOperation('seedFacven');
    setModalPhase('process');
    setModalResults(null);

    try {
      const result = await handleSeed();
      setModalResults({ success: true, ...result });
    } catch (err) {
      setModalResults({ success: false, message: err.message });
    }
  };

  const handleCorrectUnified = async () => {
    setModalVisible(true);
    setModalOperation('correctUnified');
    setModalPhase('correct');
    setModalResults(null);

    try {
      const result = await handleCorrect();
      setModalResults({ success: true, ...result });
    } catch (err) {
      setModalResults({ success: false, message: err.message });
    }
  };

  return (
    <div>
      {currentView === 'client-select' && (
        <Card 
          title="Sélectionner un Client pour Créer un Règlement" 
          bordered={false}
          extra={
            <Space>
              <Button 
                type="default" 
                icon={<DownloadOutlined />}
                onClick={handleSeedFacven}
                loading={isSeeding}
              >
                Importer depuis FTP
              </Button>
              <Button 
                type="default" 
                icon={<ToolOutlined />}
                onClick={handleCorrectUnified}
                loading={isCorrecting}
              >
                Corriger UnifiedFactureAvoir
              </Button>
            </Space>
          }
        >
          <ClientList onSelectClient={handleSelectClient} />
        </Card>
      )}
      
      {currentView === 'draft' && selectedClient && (
        <SingleReglement 
          draftClient={selectedClient}
          onChangeView={handleChangeView}
          onCompleted={handleDraftCompleted}
          onClose={handleBackToClientSelect}
        />
      )}

      {currentView === 'list' && (
        <ReglementsList 
          onSelectReglement={handleSelectReglement} 
          clientCodeFilter={listClientFilter}
          onBackToDraft={selectedClient ? () => setCurrentView('draft') : undefined}
        />
      )}
      
      {currentView === 'view' && selectedReglementId && (
        <SingleReglement 
          reglementId={selectedReglementId} 
          onChangeView={handleChangeView}
          onClose={() => {
            // Go back to where we came from
            if (previousView === 'list') {
              setCurrentView('list');
              setPreviousView(null);
            } else if (selectedClient) {
              setCurrentView('draft');
            } else {
              setCurrentView('client-select');
            }
          }}
        />
      )}
      
      {currentView === 'modify' && selectedReglementId && (
        <ModifyReglementForm reglementId={selectedReglementId} onUpdated={() => setCurrentView('view')} />
      )}
      
      {currentView === 'delete' && selectedReglementId && (
        <DeleteReglementButton reglementId={selectedReglementId} onDeleted={() => setCurrentView('list')} />
      )}

      <OperationProgressModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        operation={modalOperation}
        phase={modalPhase}
        results={modalResults}
      />
    </div>
  );
};

export default RegManagementPage;
